import { FillButton } from "PFApp/activities/parts/fill_button";
import {
  AcceptDeclineButton,
  AcceptedLabel,
  AssignButton,
  FinalLabel
} from "PFApp/activities/parts/shortlist_buttons";
import AppContext from "PFApp/app_context";
import { ProfileAvatarConnected } from "PFApp/components/profile_avatar_connected";
import { ActionIcon } from "PFComponents/action_icon";
import { Avatar } from "PFComponents/avatar";
import ButtonGroup from "PFComponents/button_group/button_group";
import { MatchAccuracyBadge } from "PFComponents/resource_metrics/match_accuracy";
import { shortlistResponseShapePropType } from "PFCore/base/prop_types/shortlist_response_prop_types";
import { getMainPosition, isProfileActive } from "PFCore/helpers/profile";
import { profileHash } from "PFCore/helpers/profile_hash";
import { ProfileNameLink } from "PFCore/helpers/profile_name_link";
import { useTemplate } from "PFCore/helpers/templates";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { FeatureFlag, PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext } from "react";

import { FillAndBookButton } from "../../../components/fill_and_book_button";
import cssDetails from "./compare_details_page.module.scss";
import css from "./compare_profile_header.less";
import GradeAndRateCell from "./parts/grade_and_rate_cell";

const CompareProfileHeader = ({
  customTypes,
  task,
  updateOne,
  comparableProfile,
  removeProfileId,
  isUnbiased,
  isFillButtonDisabled,
  fetchVacancies
}) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);

  const isPermittedTo = useIsPermittedTo(currentProfile);
  const isAutoBookingCreationEnabled = useIsFeatureEnabled()(FeatureFlag.AutoBookingCreation);

  const { state, profile } = comparableProfile;
  const isReviewer = task.reviewers?.find(({ profile_id }) => profile_id === currentProfile.id);
  const resourcerPermissionGroup = currentProfile.permissions_group.permissions.find(
    ({ name, permitted }) => name === "resourcer" && permitted
  );
  const isActive = isProfileActive(comparableProfile.profile);
  const isResourcer = Boolean(resourcerPermissionGroup?.permitted);
  const hasAcceptForEndUser = isPermittedTo(PermissionRule.AcceptForEndUser);
  const normalizedScore = comparableProfile.scores?.normalized_score;
  const canFill = task.state !== "draft" && task.coowned && isResourcer && isActive;
  const canInvite = task.state !== "draft" && (task.coowned || isReviewer) && isActive;
  const canAcceptDecline = !isAudit && state === "pending" && (isReviewer || task.coowned) && isActive;
  const isAssigned = comparableProfile.state === "assigned";
  const template = useTemplate(task.template_id);
  const isAudit = template?.key.includes("audit");
  const buttonsProps = {
    shortlist: comparableProfile,
    isTaskOwner: task.coowned,
    onDone: updateOne,
    qaPrefix: "Compare"
  };

  const getValues = (name) => profile[name] || getMainPosition(profile)[name] || [];

  const getValue = (name) => getValues(name)[0];

  const renderActionButton = () =>
    isAutoBookingCreationEnabled ? (
      <FillAndBookButton
        item={comparableProfile}
        task={task}
        isFillButtonDisabled={isFillButtonDisabled}
        fetchVacancies={fetchVacancies}
      />
    ) : (
      <FillButton
        item={comparableProfile}
        onDone={updateOne}
        qaPrefix="Compare"
        kind="buttonGroup"
        style={{ flex: 1 }}
        isUnbiased={isUnbiased}
        disabled={isFillButtonDisabled}
        onSuccess={fetchVacancies}
      />
    );

  return (
    <th className={cssDetails.th}>
      <div className={css.profileHeader}>
        {isUnbiased ? (
          <Avatar size={30} backgroundColor="#ffc0cb" text={profileHash(profile.id)} />
        ) : (
          <ProfileAvatarConnected profile={profile} size={30} smartLink />
        )}

        {isUnbiased ? (
          <div className={css.unbiasedCandidate}>{`Candidate ${profileHash(profile.id)}`}</div>
        ) : (
          <div className={css.name}>
            &nbsp; {/* handle flex empty cell */}
            <span className={css.nameInner}>
              <ProfileNameLink profile={profile} className={css.profileLink} />
            </span>
          </div>
        )}

        {normalizedScore && (
          <div className={css.rating}>
            <MatchAccuracyBadge value={normalizedScore} />
          </div>
        )}

        <ActionIcon name="cross" size="xs" onClick={() => removeProfileId(profile.id)} />
      </div>

      <div className={css.buttons}>
        {canAcceptDecline && (
          <ButtonGroup style={{ display: "flex" }}>
            <AcceptDeclineButton action="accept" {...buttonsProps} />
            <AcceptDeclineButton action="decline" {...buttonsProps} />
          </ButtonGroup>
        )}

        {(state === "accepted" || state === "assigned") && (
          <div className={css.acceptedHeader}>
            {template?.shortlist_invites && canInvite ? (
              isAssigned && canFill && hasAcceptForEndUser ? (
                renderActionButton()
              ) : (
                <AssignButton
                  {...buttonsProps}
                  kind="buttonGroup"
                  style={{ flex: 1 }}
                  isUnbiased={isUnbiased}
                />
              )
            ) : canFill ? (
              renderActionButton()
            ) : (
              <>&nbsp;</>
            )}
            <AcceptedLabel style={{ flex: 1 }} {...buttonsProps} />
          </div>
        )}

        {["declined", "rejected", "filled", "booked", "confirmed"].includes(state) && (
          <FinalLabel kind={state} {...buttonsProps} />
        )}
      </div>
      <GradeAndRateCell customTypes={customTypes} profile={profile} getValue={getValue} />
    </th>
  );
};

CompareProfileHeader.propTypes = {
  customTypes: PropTypes.any.isRequired,
  task: PropTypes.object.isRequired,
  comparableProfile: shortlistResponseShapePropType,
  updateOne: PropTypes.func.isRequired,
  removeProfileId: PropTypes.func.isRequired,
  isUnbiased: PropTypes.bool,
  isActivityUnbiased: PropTypes.bool,
  isFillButtonDisabled: PropTypes.bool.isRequired,
  fetchVacancies: PropTypes.func.isRequired
};

export default CompareProfileHeader;
